import { useCallback, useEffect, useMemo, useState } from 'react'
import { createFileRoute, Link, useLocation } from '@tanstack/react-router'
import { Plus, Pencil, Lock, MoreHorizontal, RotateCcw, AlertTriangle } from 'lucide-react'
import { Skeleton, Table, Switch, ActionIcon, Group, Tooltip, Menu, Modal, Button, Text } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'

import {
  getTaxonomyVersion,
  listClauseTypes,
  listTaxonomyChanges,
  listTaxonomyOrphans,
  listTaxonomyVersions,
  resetClauseType,
  setClauseTypeCore,
  setClauseTypeHidden,
  publishTaxonomy,
  TAXONOMY_CATEGORIES,
  type ClauseTypeAdminDTO,
  type TaxonomyCategory,
  type TaxonomyOrphan,
} from '@/services/taxonomyAdminService'
import { colorForCode } from '@/lib/taxonomyColors'
import { PageHeader } from '@/components/page/PageHeader'
import { useAdminScope } from '../-admin/adminScope'
import { PublishBar, PublishDialog, type PublishChangeItem } from '../-admin/PublishBar'
import { ClauseTypeEditorModal } from '../-taxonomy/ClauseTypeEditorModal'
import { diffWorkingSet, type DraftChange } from '../-taxonomy/taxonomyDraft'
import { changeDetail, fieldLabel } from '../-taxonomy/overrides'
import '../-taxonomy/taxonomy.css'

export const Route = createFileRoute('/_app/organization/admin/taxonomy')({
  head: () => ({ meta: [{ title: 'Taxonomy · Admin · AERRIS' }] }),
  component: TaxonomyTab,
})

/** "COMMERCIAL" → "Commercial", for tabs and messages. */
const categoryLabel = (c: TaxonomyCategory) => c.charAt(0) + c.slice(1).toLowerCase()

/** A category's live version and its unpublished changes: undefined while loading. */
interface CategoryStatus {
  version: number | null
  /** The server's diff of the working set against the live version. */
  changes?: DraftChange[]
  /** Read only when the changes endpoint failed: the live payload, diffed here instead. */
  fallbackPayload?: Record<string, unknown>[] | null
  /** Neither could be read, so drafts are unknown. */
  failed?: boolean
}

/** A scope overrides a type when it holds a field of its own over a shipped base. */
const isOverridden = (t: ClauseTypeAdminDTO) => !t.clientDefined && (t.overriddenFields?.length ?? 0) > 0

function TaxonomyTab() {
  const { customerUuid, globalMode, clientId, canManage, accessLevel, scopeName, loading: scopeLoading } = useAdminScope()

  // A ?cat= deep link (e.g. from the prompt injection resolver) preselects the category.
  const catParam = (useLocation().search as Record<string, string | undefined>).cat
  const initialCat = (TAXONOMY_CATEGORIES as readonly string[]).includes(catParam ?? '')
    ? (catParam as TaxonomyCategory)
    : 'COMMERCIAL'
  const [category, setCategory] = useState<TaxonomyCategory>(initialCat)
  const [types, setTypes] = useState<ClauseTypeAdminDTO[]>([])
  const [typesLoading, setTypesLoading] = useState(true)
  const [publishing, setPublishing] = useState(false)
  const [publishOpen, setPublishOpen] = useState(false)
  const [editing, setEditing] = useState<ClauseTypeAdminDTO | null>(null)
  const [editorOpened, { open: openEditor, close: closeEditor }] = useDisclosure(false)
  const [status, setStatus] = useState<Partial<Record<TaxonomyCategory, CategoryStatus>>>({})
  const [orphans, setOrphans] = useState<TaxonomyOrphan[]>([])
  const [resetTarget, setResetTarget] = useState<ClauseTypeAdminDTO | null>(null)
  const [resetting, setResetting] = useState(false)
  // Bumped after every write (edit, toggle, reset, publish) so the server's view is read again.
  const [statusKey, setStatusKey] = useState(0)

  const inScope = !scopeLoading && canManage && (globalMode || Boolean(clientId))
  // The orphan rows a viewer may reset: a client's own, or the global default's.
  const ownScope: TaxonomyOrphan['scope'] = customerUuid ? 'client' : 'global'

  // Every category's live version and unpublished changes (the Draft pills, the tab dots and the
  // publish bar). The changes are the server's diff; if that cannot be read, the live payload is
  // read instead and diffed here.
  useEffect(() => {
    if (!inScope) {
      setStatus({})
      return
    }
    let cancelled = false
    for (const cat of TAXONOMY_CATEGORIES) {
      listTaxonomyVersions(customerUuid, cat)
        .then(async (vs) => {
          const version = vs.find((v) => v.current)?.version ?? null
          let next: CategoryStatus
          try {
            next = { version, changes: await listTaxonomyChanges(customerUuid, cat) }
          } catch {
            const payload = version == null ? null : (await getTaxonomyVersion(customerUuid, cat, version)).payload
            next = { version, fallbackPayload: payload }
          }
          if (!cancelled) setStatus((prev) => ({ ...prev, [cat]: next }))
        })
        .catch(() => {
          if (!cancelled) setStatus((prev) => ({ ...prev, [cat]: { version: null, failed: true } }))
        })
    }
    return () => { cancelled = true }
  }, [customerUuid, inScope, statusKey])

  // Override rows for codes the bundle no longer ships, in the category on screen.
  useEffect(() => {
    if (!inScope) {
      setOrphans([])
      return
    }
    let cancelled = false
    listTaxonomyOrphans(customerUuid, category)
      .then((o) => { if (!cancelled) setOrphans(Array.isArray(o) ? o : []) })
      .catch(() => { if (!cancelled) setOrphans([]) })
    return () => { cancelled = true }
  }, [customerUuid, inScope, category, statusKey])

  const fetchTypes = useCallback(async () => {
    if (!canManage || (!globalMode && !clientId)) return []
    return listClauseTypes(customerUuid)
  }, [customerUuid, globalMode, clientId, canManage])

  const load = useCallback(async () => {
    if (scopeLoading) return
    setTypesLoading(true)
    try {
      setTypes(await fetchTypes())
    } catch (e) {
      console.error('Failed to load taxonomy:', e)
      notifications.show({ color: 'red', title: 'Could not load taxonomy', message: 'Please try again.' })
    } finally {
      setTypesLoading(false)
    }
  }, [fetchTypes, scopeLoading])

  // After a write: re-read the working set without the skeleton, and the server's diff with it.
  const refresh = useCallback(async () => {
    try {
      setTypes(await fetchTypes())
    } catch (e) {
      console.error('Failed to reload taxonomy:', e)
    }
    setStatusKey((k) => k + 1)
  }, [fetchTypes])

  useEffect(() => {
    void load()
  }, [load])

  const loading = scopeLoading || typesLoading

  // Unpublished changes per category; undefined while that category's status loads (or when it
  // could not be read).
  const draftsByCategory = useMemo(() => {
    const out: Partial<Record<TaxonomyCategory, DraftChange[]>> = {}
    for (const cat of TAXONOMY_CATEGORIES) {
      const s = status[cat]
      if (!s || s.failed) continue
      if (s.changes) out[cat] = Array.isArray(s.changes) ? s.changes : []
      else if (s.fallbackPayload !== undefined) {
        out[cat] = diffWorkingSet(types.filter((t) => t.category === cat), s.fallbackPayload)
      }
    }
    return out
  }, [types, status])

  const drafts = draftsByCategory[category]
  const draftByCode = useMemo(() => new Map((drafts ?? []).map((d) => [d.code, d])), [drafts])
  const currentVersion = status[category]?.version ?? null

  const rows = useMemo(
    () => types.filter((t) => t.category === category).sort((a, b) => a.label.localeCompare(b.label)),
    [types, category],
  )

  const sameType = (a: ClauseTypeAdminDTO, b: ClauseTypeAdminDTO) => a.category === b.category && a.code === b.code

  const toggleCore = async (t: ClauseTypeAdminDTO) => {
    try {
      await setClauseTypeCore(customerUuid, t.category, t.code, !t.core)
      setTypes((prev) => prev.map((x) => (sameType(x, t) ? { ...x, core: !t.core } : x)))
      void refresh()
    } catch {
      notifications.show({ color: 'red', message: 'Could not update core flag.' })
    }
  }

  const toggleActive = async (t: ClauseTypeAdminDTO) => {
    try {
      await setClauseTypeHidden(customerUuid, t.category, t.code, t.active) // active → hide
      setTypes((prev) => prev.map((x) => (sameType(x, t) ? { ...x, active: !t.active } : x)))
      void refresh()
    } catch {
      notifications.show({ color: 'red', message: 'Could not change visibility.' })
    }
  }

  /** Drop every override this scope holds for a code — a row's, or an orphan's. */
  const resetToShipped = async (code: string, label: string) => {
    setResetting(true)
    try {
      await resetClauseType(customerUuid, category, code)
      setResetTarget(null)
      notifications.show({
        color: 'green',
        title: `${label} reset to shipped`,
        message: `Publish the ${categoryLabel(category)} taxonomy to apply it to extraction.`,
      })
      await refresh()
    } catch {
      notifications.show({ color: 'red', title: 'Could not reset', message: 'The server rejected the reset.' })
    } finally {
      setResetting(false)
    }
  }

  const handlePublish = async (note: string) => {
    setPublishing(true)
    try {
      const v = await publishTaxonomy(customerUuid, category, note || undefined)
      setPublishOpen(false)
      setStatusKey((k) => k + 1)
      notifications.show({
        color: 'green',
        title: `${categoryLabel(category)} taxonomy published`,
        message: `Version ${v.version} of the ${category.toLowerCase()} taxonomy for ${scopeName} is now live for extraction and dashboards.`,
      })
    } catch (error) {
      console.error('Publish failed:', error)
      notifications.show({
        color: 'red',
        title: 'Publish blocked',
        message: 'Publishing was rejected — a validation or dry-run check failed, or you lack admin access.',
      })
    } finally {
      setPublishing(false)
    }
  }

  const openCreate = () => {
    setEditing(null)
    openEditor()
  }
  const openEdit = (t: ClauseTypeAdminDTO) => {
    setEditing(t)
    openEditor()
  }

  const publishItems: PublishChangeItem[] = (drafts ?? []).map((d) => ({
    key: d.code,
    label: d.label,
    detail: changeDetail(d),
  }))
  const nextVersion = (currentVersion ?? 0) + 1
  const snapshotFailed = status[category]?.failed === true
  const definedTag = customerUuid ? 'Client-defined' : 'Global-defined'

  return (
    <div className="taxonomy-page">
      <PageHeader
        title="Taxonomy"
        subtitle="The clause types the AI looks for in each contract category. Changes stay in draft until you publish."
        actions={
          <button type="button" className="page-btn page-btn--primary" onClick={openCreate} disabled={!canManage}>
            <Plus size={16} />
            New clause type
          </button>
        }
      />

      <div className="taxonomy-tabs-row">
        <div className="taxonomy-tabs" role="tablist" aria-label="Contract category">
          {TAXONOMY_CATEGORIES.map((c) => {
            const count = types.filter((t) => t.category === c).length
            const dirty = (draftsByCategory[c]?.length ?? 0) > 0
            return (
              <button
                key={c}
                type="button"
                role="tab"
                aria-selected={c === category}
                className={`taxonomy-tab ${c === category ? 'is-active' : ''}`}
                onClick={() => setCategory(c)}
              >
                {categoryLabel(c)}
                {canManage && <span className="taxonomy-tab__count">{count}</span>}
                {dirty && <span className="taxonomy-tab__dot" title="Unpublished changes" aria-label="Unpublished changes" />}
              </button>
            )
          })}
        </div>
        {canManage && (
          <span className="admin-version-pointer" data-testid="taxonomy-version-pointer">
            {currentVersion != null
              ? `Published v${currentVersion}`
              : customerUuid
                // A client with no version of its own is served the global default's (ADR 0043 D4).
                ? 'Uses the global default'
                : 'Unpublished'}
            {' · '}
            <Link to="/organization/admin/versions" search={(prev) => ({ ...prev, sel: `tax:${category}` })}>
              View history
            </Link>
          </span>
        )}
      </div>

      {canManage && !loading && orphans.length > 0 && (
        <div className="notice taxonomy-orphans" role="status" data-testid="taxonomy-orphans">
          <AlertTriangle size={16} />
          <div style={{ flex: 1 }}>
            <div>
              No longer shipped: {orphans.map((o) => o.code).join(', ')} — overrides kept, not in use
            </div>
            <Group gap="xs" mt={6}>
              {orphans.filter((o) => o.scope === ownScope).map((o) => (
                <Button
                  key={o.code}
                  size="compact-xs"
                  variant="subtle"
                  color="gray"
                  leftSection={<RotateCcw size={12} />}
                  onClick={() => resetToShipped(o.code, o.code)}
                  disabled={resetting}
                  title={`Overrides: ${o.fields.map(fieldLabel).join(', ')}`}
                  data-testid={`taxonomy-orphan-reset-${o.code}`}
                >
                  Reset {o.code}
                </Button>
              ))}
            </Group>
          </div>
        </div>
      )}

      {loading ? (
        <div className="taxonomy-skeleton" aria-busy="true" aria-label="Loading taxonomy">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} height={34} radius="sm" />
          ))}
        </div>
      ) : !canManage ? (
        <div className="notice">
          <Lock size={16} />
          {globalMode
            ? 'Editing the global default requires platform-admin access.'
            : !clientId
              ? 'Select a client to manage its taxonomy.'
              : `You need admin access to ${scopeName} to edit its taxonomy${accessLevel ? ` (your access: ${accessLevel})` : ''}.`}
        </div>
      ) : rows.length === 0 ? (
        <div className="taxonomy-empty page-card">
          <h2 className="taxonomy-empty-title">No clause types in {categoryLabel(category)} yet</h2>
          <p className="taxonomy-empty-text">
            Add a clause type to start classifying {category.toLowerCase()} contracts. Publish the category
            when you’re ready to apply it to extraction and dashboards.
          </p>
          <button type="button" className="page-btn page-btn--primary" onClick={openCreate}>
            <Plus size={16} />
            New clause type
          </button>
        </div>
      ) : (
        <div className="taxonomy-table-wrap page-card" key={category}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Clause type</Table.Th>
                <Table.Th>Code</Table.Th>
                <Table.Th style={{ width: 88 }}>
                  <Tooltip label="Core types power the specialised risk dashboards (Termination, Liability, …)." withArrow>
                    <span>Core</span>
                  </Tooltip>
                </Table.Th>
                <Table.Th style={{ width: 88 }}>
                  <Tooltip label="Inactive types are hidden from reviewers and skipped during extraction." withArrow>
                    <span>Active</span>
                  </Tooltip>
                </Table.Th>
                <Table.Th style={{ width: 88 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((t) => {
                const draft = draftByCode.get(t.code)
                const overridden = isOverridden(t)
                return (
                  <Table.Tr
                    key={`${t.category}:${t.code}`}
                    className={t.active ? undefined : 'taxonomy-row--inactive'}
                    data-testid={`taxonomy-row-${t.code}`}
                  >
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <span className="taxonomy-dot" style={{ background: t.color || colorForCode(t.code) }} />
                        <span className="taxonomy-label">{t.label}</span>
                        {draft && (
                          <span className="admin-draft-pill" title={changeDetail(draft)}>Draft</span>
                        )}
                        {overridden && (
                          <Tooltip
                            label={`Overridden: ${(t.overriddenFields ?? []).map(fieldLabel).join(', ')}`}
                            withArrow
                          >
                            <span className="admin-override-pill" data-testid={`taxonomy-overridden-${t.code}`}>
                              Overridden
                            </span>
                          </Tooltip>
                        )}
                        {t.clientDefined && (
                          <span className="admin-defined-tag" data-testid={`taxonomy-defined-${t.code}`}>
                            {definedTag}
                          </span>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <span className="taxonomy-code">{t.code}</span>
                    </Table.Td>
                    <Table.Td>
                      <Switch size="sm" checked={t.core} onChange={() => toggleCore(t)} aria-label={`Core type: ${t.label}`} />
                    </Table.Td>
                    <Table.Td>
                      <Switch size="sm" checked={t.active} onChange={() => toggleActive(t)} aria-label={`Active: ${t.label}`} />
                    </Table.Td>
                    <Table.Td>
                      <Group gap={2} wrap="nowrap" justify="flex-end">
                        <Tooltip label="Edit">
                          <ActionIcon variant="subtle" color="gray" onClick={() => openEdit(t)} aria-label={`Edit ${t.label}`}>
                            <Pencil size={16} />
                          </ActionIcon>
                        </Tooltip>
                        {overridden && (
                          <Menu position="bottom-end" withinPortal>
                            <Menu.Target>
                              <ActionIcon
                                variant="subtle"
                                color="gray"
                                aria-label={`More actions for ${t.label}`}
                                data-testid={`taxonomy-row-menu-${t.code}`}
                              >
                                <MoreHorizontal size={16} />
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item
                                leftSection={<RotateCcw size={14} />}
                                onClick={() => setResetTarget(t)}
                                data-testid={`taxonomy-reset-${t.code}`}
                              >
                                Reset to shipped
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          </Table>
        </div>
      )}

      {canManage && !loading && (
        <PublishBar
          count={snapshotFailed ? null : drafts?.length ?? 0}
          what={`the ${categoryLabel(category)} taxonomy`}
          nextVersion={snapshotFailed ? null : nextVersion}
          onReview={() => setPublishOpen(true)}
        />
      )}

      <PublishDialog
        opened={publishOpen}
        onClose={() => setPublishOpen(false)}
        title={`Publish the ${categoryLabel(category)} taxonomy`}
        changes={
          publishItems.length
            ? publishItems
            : [{ key: 'all', label: `${categoryLabel(category)} taxonomy`, detail: 'Republish the working set' }]
        }
        onPublish={handlePublish}
        busy={publishing}
        confirmLabel={snapshotFailed ? 'Publish' : `Publish v${nextVersion}`}
      />

      <Modal
        opened={resetTarget !== null}
        onClose={() => { if (!resetting) setResetTarget(null) }}
        title="Reset to shipped"
        centered
      >
        {resetTarget && (
          <>
            <Text size="sm">
              {resetTarget.label} goes back to the shipped wording for{' '}
              {(resetTarget.overriddenFields ?? []).map(fieldLabel).join(', ')}. It applies once you publish
              the {categoryLabel(category)} taxonomy.
            </Text>
            <Group justify="flex-end" mt="lg">
              <Button variant="default" onClick={() => setResetTarget(null)} disabled={resetting}>
                Cancel
              </Button>
              <Button
                onClick={() => resetToShipped(resetTarget.code, resetTarget.label)}
                loading={resetting}
                data-testid="taxonomy-reset-confirm"
              >
                Reset to shipped
              </Button>
            </Group>
          </>
        )}
      </Modal>

      <ClauseTypeEditorModal
        opened={editorOpened}
        onClose={closeEditor}
        customerUuid={customerUuid}
        editing={editing}
        defaultCategory={category}
        codeOptions={types.map((t) => ({ code: t.code, label: t.label, category: t.category }))}
        onSaved={refresh}
      />
    </div>
  )
}
